import Button from '@/shared/Button'
import FormItem from '@/shared/FormCreator/FormItem'
import { yupResolver } from '@hookform/resolvers/yup'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'

import FormDatePicker from '@/shared/FormCreator/FormItem/FormDatePicker'
import FormTimePicker from '@/shared/FormCreator/FormItem/FormTimePicker'
import isEqual from 'lodash/isEqual'
import uniqBy from 'lodash/uniqBy'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import * as Yup from 'yup'

import debounce from '@mui/material/utils/debounce'

import EmailDialog from '@/components/EmailDialog'

import { ReactComponent as DeleteIcon } from '@/assets/trash_bin.svg'
import CourseDialog from '@/components/CourseDialog'
import { useNavigate } from 'react-router-dom'
import combineDateTime from '../libs/utils/combineDateTime'

const mapStateToProps = ({ campaign, loading }) => ({
  options: campaign.createCampaignOptions,
  isProgramsLoading: loading['courseProgram/getCourseProgram'],
  isUserGroupsLoading: loading['userGroup/getUserGroups'],
})

const Section = ({ title, children }) => (
  <Box>
    <Paper elevation={2} sx={{ borderRadius: 0, padding: 1 }}>
      <Typography textAlign="center">{title}</Typography>
    </Paper>
    <CardContent sx={{ padding: 3, mt: 1, mb: 2 }}>{children}</CardContent>
  </Box>
)

const inputConfig = { inputBaseProps: { flexDirection: 'column', alignItems: 'flex-start' } }

const CampaignCreate = ({ dispatch, options, isProgramsLoading, isUserGroupsLoading }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [preview, setPreview] = useState({})
  const [openCourse, setOpenCourse] = useState(false)

  Yup.addMethod(Yup.date, 'minStartDate', function testStartDate(errorMessage1, errorMessage2) {
    return this.test('test-start-date', (errorMessage1, errorMessage2), (_, context) => {
      const { parent = {}, path, createError } = context

      const { startDate, startTime, dateRangeType } = parent

      const isStartFixed = isEqual(dateRangeType, '1')
      const startDateTime = combineDateTime(startDate, startTime)

      if (moment(startDateTime).isAfter(moment().add(14, 'minutes')) && !isStartFixed) {
        return true
      }
      if (moment(startDateTime).isAfter(moment().subtract(1, 'minutes')) && isStartFixed) {
        return true
      }

      // isStartFixed === true === now, isStartFixed === false === + 15 minutes
      return isStartFixed
        ? createError({ path, message: errorMessage2 })
        : createError({ path, message: errorMessage1 })
    })
  })

  Yup.addMethod(Yup.date, 'afterStartDate', function testEndDate(errorMessage) {
    return this.test('test-end-date', errorMessage, (_, context) => {
      const { parent = {}, path, createError } = context

      const { startDate, startTime, endDate, endTime } = parent

      const startDateTime = combineDateTime(startDate, startTime)
      const endDateTime = combineDateTime(endDate, endTime)

      if (moment(endDateTime).isSameOrAfter(moment(startDateTime).add(1, 'days'))) {
        return true
      }

      return createError({ path, message: errorMessage })
    })
  })

  Yup.addMethod(Yup.array, 'checkReminderDates', function testReminderDate(errorMessage) {
    return this.test('test-reminder-date', errorMessage, (_, context) => {
      const { parent = {}, path, createError } = context

      const {
        startDate,
        endDate,
        startTime,
        endTime,
        reminderScheduleList = [],
        reminderNotify,
      } = parent

      if (!reminderNotify) {
        return true
      }

      const startDateTime = combineDateTime(startDate, startTime)
      const endDateTime = combineDateTime(endDate, endTime)

      const error = createError({ path, message: errorMessage })
      const outofRangeDate = reminderScheduleList.find(
        d =>
          !moment(d).isBetween(
            moment(startDateTime).endOf('minute'),
            moment(endDateTime).startOf('minute'),
          ),
      )

      if (outofRangeDate !== undefined || outofRangeDate === false) {
        return error
      }

      const hasDuplicate =
        uniqBy(reminderScheduleList, element => element.getTime()).length !==
        reminderScheduleList.length

      if (hasDuplicate) {
        return error
      }

      return true
    })
  })

  const programInputRef = useRef()
  const userGroupInputRef = useRef()

  const [programInput, setProgramInput] = useState('')
  const [programResults, setProgramResults] = useState({})
  const [programPage, setProgramPage] = useState(0)

  const [userGroupInput, setUserGroupInput] = useState('')
  const [userGroupResults, setUserGroupResults] = useState({})
  const [userGroupPage, setUserGroupPage] = useState(0)

  const { data: programResultsData = [], paging: programPaging } = programResults
  const { data: userGroupResultsData = [], paging: userGroupPaging } = userGroupResults

  // Get Course Programs
  const fetchPrograms = useCallback(() => {
    dispatch({
      type: 'courseProgram/getCourseProgram',
      payload: { searchParam: programInputRef.current, pageIndex: programPage, pageSize: 10 },
      successCallback: response => {
        const { data: responseData, paging: responsePaging } = response
        const { pageIndex } = responsePaging

        const newData = responseData.map(d => ({ id: d.programId, label: d.name }))

        setProgramResults(s => ({
          data: pageIndex > 0 ? [...(s.data || []), ...newData] : newData,
          paging: responsePaging,
        }))
      },
    })
  }, [dispatch, programPage])

  const loadMoreProgramsDebounce = debounce(() => {
    setProgramPage(p => p + 1)
  }, 400)

  const fetchProgramsDebounce = useMemo(
    () =>
      debounce(() => {
        fetchPrograms()
      }, 400),
    [fetchPrograms],
  )

  useEffect(() => {
    fetchProgramsDebounce()
  }, [programInput, programPage, fetchProgramsDebounce])

  // Get User Groups
  const fetchUserGroups = useCallback(() => {
    dispatch({
      type: 'userGroup/getUserGroups',
      payload: {
        pageIndex: userGroupPage,
        pageSize: 10,
        searchGroupName: userGroupInputRef.current,
      },
      successCallback: response => {
        const { data: responseData, paging: responsePaging } = response
        const { pageIndex } = responsePaging

        const newData = responseData.map(d => ({ id: d.groupId, label: d.groupName }))

        setUserGroupResults(s => ({
          data: pageIndex > 0 ? [...(s.data || []), ...newData] : newData,
          paging: responsePaging,
        }))
      },
    })
  }, [dispatch, userGroupPage])

  const loadMoreUserGroupsDebounce = debounce(() => {
    setUserGroupPage(p => p + 1)
  }, 400)

  const fetchUserGroupsDebounce = useMemo(
    () =>
      debounce(() => {
        fetchUserGroups()
      }, 400),
    [fetchUserGroups],
  )

  useEffect(() => {
    fetchUserGroupsDebounce()
  }, [userGroupInput, userGroupPage, fetchUserGroupsDebounce])

  const { completionMail, remindMail, trainingMail } = options || {}

  const validations = {
    name: Yup.string().required(t('validation.campaign_name_required')),
    userGroup: Yup.object()
      .required(t('validation.user_group_required'))
      .typeError(t('validation.user_group_required')),
    course: Yup.object()
      .required(t('validation.course_required'))
      .typeError(t('validation.user_group_required')),
    startDate: Yup.date().minStartDate(
      t('validation.min_startdate'),
      t('validation.min_startdate2'),
    ),
    startTime: Yup.date().typeError('').minStartDate('', ''),
    endDate: Yup.date().afterStartDate(t('validation.after_startdate')),
    endTime: Yup.date().typeError('').afterStartDate(''),
    reminderScheduleList: Yup.array().checkReminderDates(t('validation.reminder_dates')),
  }

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    // eslint-disable-next-line no-unused-vars
    formState: { errors },
    reset: formReset,
  } = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(Yup.object().shape(validations)),
  })

  const isStartTimeFixed = isEqual(watch('dateRangeType'), '1')
  const isReminderNotifyChecked = watch('reminderNotify')
  const selectedCourse = watch('course')

  const reminderScheduleAddHandler = () => {
    const list = getValues('reminderScheduleList')
    setValue('reminderScheduleList', [
      ...list,
      moment().set({ second: 0, millisecond: 0 }).toDate(),
    ])
  }

  const updateReminderScheduleAt = (value, index) => {
    const list = getValues('reminderScheduleList')

    if (index < list.length) {
      list[index] = value
      setValue('reminderScheduleList', [...list])
    }
  }

  const removeReminderScheduleAt = index => {
    const list = getValues('reminderScheduleList')

    if (index < list.length) {
      list.splice(index, 1)
      setValue('reminderScheduleList', [...list])
    }
  }

  useEffect(() => {
    const list = getValues('reminderScheduleList')
    if (list.length === 0 && isReminderNotifyChecked === true) {
      setValue('reminderScheduleList', [moment().set({ second: 0, millisecond: 0 }).toDate()])
    }
    if (list.length > 0 && isReminderNotifyChecked === false) {
      setValue('reminderScheduleList', [])
    }
  }, [getValues, setValue, isReminderNotifyChecked])

  const scheduleComponent = ({ value, index, disabled }) => (
    <Box display="flex" columnGap={1}>
      <IconButton
        disabled={disabled}
        onClick={() => {
          removeReminderScheduleAt(index)
        }}
        color="secondary_80"
      >
        <DeleteIcon />
      </IconButton>
      <FormDatePicker
        itemProps={{ disabled }}
        formProps={{
          field: {
            value,
            onChange: val => {
              const newDate = moment(val)
              updateReminderScheduleAt(
                moment(value)
                  .set({ year: newDate.year(), month: newDate.month(), date: newDate.date() })
                  .toDate(),
                index,
              )
            },
          },
        }}
        inputConfig={{ sx: { width: 180 } }}
      />
      <FormTimePicker
        itemProps={{ disabled }}
        formProps={{
          field: {
            value,
            onChange: val => {
              const newDate = moment(val)
              updateReminderScheduleAt(
                moment(value).set({ hour: newDate.hour(), minute: newDate.minute() }).toDate(),
                index,
              )
            },
          },
        }}
        inputConfig={{ sx: { width: 180, ml: 1 } }}
      />
    </Box>
  )

  const onSubmit = values => {
    const {
      completeNotify,
      course,
      startDate,
      startTime,
      endDate,
      endTime,
      name,
      reminderNotify,
      sendInvite,
      userGroup,
      reminderScheduleList,
    } = values

    const startDateTime = isStartTimeFixed
      ? moment().toDate()
      : combineDateTime(startDate, startTime)

    const endDateTime = combineDateTime(endDate, endTime)

    const payload = {
      trainingCampName: name,
      userGroupId: userGroup.id,
      courseProgramId: course.id,
      notiCompleteOrNot: completeNotify,
      reminderOrNot: reminderNotify,
      invitationOrNot: sendInvite,
      initialTime: startDateTime,
      endTime: endDateTime,
    }

    if (reminderNotify) {
      payload.remindTime = reminderScheduleList
    }

    dispatch({
      type: 'campaign/createCampaign',
      payload,
      successCallback: () => {
        dispatch({
          type: 'app/notify',
          payload: { message: t('messages.campaign_created'), severity: 'success' },
        })
        navigate('/campaign', { replace: true })
      },
    })

    formReset?.({}, { keepValues: true })
  }

  useEffect(() => {
    if (isStartTimeFixed) {
      setValue?.('startDate', new Date())
      setValue?.('startTime', moment().toDate())

      setValue?.('endDate', moment().add(1, 'days').toDate())
      setValue?.('endTime', moment().add(1, 'days').toDate())
    } else {
      setValue?.('startDate', moment().add(20, 'minutes').toDate())
      setValue?.('startTime', moment().add(20, 'minutes').toDate())

      setValue?.('endDate', moment().add(20, 'minutes').add(1, 'days').toDate())
      setValue?.('endTime', moment().add(20, 'minutes').add(1, 'days').toDate())
    }
  }, [isStartTimeFixed, setValue])

  useEffect(() => {
    dispatch({ type: 'campaign/getCampaignOptions' })
  }, [dispatch])

  return (
    <Container maxWidth={false}>
      <Card>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit, () => {
            trigger?.()
          })}
        >
          <Section title={t('general.basic_info')}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormItem
                  label={t('general.campaign_name')}
                  defaultValue=""
                  name="name"
                  control={control}
                  inputConfig={{ ...inputConfig, inputProps: { maxLength: 40 } }}
                  errors={errors}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormItem
                  label={t('general.select_users')}
                  defaultValue={null}
                  name="userGroup"
                  type="autocomplete"
                  control={control}
                  errors={errors}
                  inputConfig={{
                    ...inputConfig,
                    sx: { width: 200, mt: 1 },
                    onInputChange: (_, value, reason) => {
                      userGroupInputRef.current = value

                      if (['input', 'clear'].includes(reason)) {
                        setUserGroupPage(0)
                        setUserGroupInput(value)
                      }
                    },
                    onFocus: () => {
                      fetchUserGroupsDebounce()
                    },
                    onLoadMore: () => {
                      const { hasNext } = userGroupPaging

                      if (!isUserGroupsLoading && hasNext) {
                        loadMoreUserGroupsDebounce()
                      }
                    },
                    options: userGroupResultsData,
                    filterOptions: opts => opts,
                    isOptionEqualToValue: (option, value) => option.id === value.id,
                    renderOption: (renderProps, option) => (
                      <Box component="li" {...renderProps} key={option.id}>
                        {option.label}
                      </Box>
                    ),
                  }}
                />
              </Grid>
              <Grid item container xs={12} spacing={1}>
                <Grid item>
                  <FormItem
                    label={t('general.select_course')}
                    defaultValue={null}
                    name="course"
                    type="autocomplete"
                    control={control}
                    errors={errors}
                    inputConfig={{
                      ...inputConfig,
                      sx: { width: 200, mt: 1 },
                      onInputChange: (_, value, reason) => {
                        programInputRef.current = value

                        if (['input', 'clear'].includes(reason)) {
                          setProgramPage(0)
                          setProgramInput(value)
                        }
                      },
                      onFocus: () => {
                        fetchProgramsDebounce()
                      },
                      onLoadMore: () => {
                        const { hasNext } = programPaging

                        if (!isProgramsLoading && hasNext) {
                          loadMoreProgramsDebounce()
                        }
                      },
                      options: programResultsData,
                      filterOptions: opts => opts,
                      isOptionEqualToValue: (option, value) => option.id === value.id,
                      renderOption: (renderProps, option) => (
                        <Box component="li" {...renderProps} key={option.id}>
                          {option.label}
                        </Box>
                      ),
                    }}
                  />
                </Grid>

                <Grid item>
                  <Button
                    disabled={!selectedCourse}
                    sx={{ mt: { xs: 0, sm: '75%' } }}
                    color="white"
                    onClick={() => {
                      setOpenCourse(true)
                    }}
                  >
                    {t('general.preview')}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Section>

          <Section title={t('general.set_schedule')}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormItem
                  defaultValue={2}
                  name="dateRangeType"
                  type="radio"
                  control={control}
                  inputConfig={{
                    ...inputConfig,
                    options: [
                      { value: 1, label: t('general.now', { num: 1 }) },
                      { value: 2, label: t('general.custom_time', { num: 2 }) },
                    ],
                  }}
                  errors={errors}
                />
              </Grid>
              <Grid container item xs={12} spacing={1} alignItems="flex-start">
                <Grid item>
                  <FormItem
                    disabled={isStartTimeFixed}
                    label={t('general.start_time')}
                    defaultValue={moment().startOf('day').toDate()}
                    name="startDate"
                    type="date"
                    control={control}
                    inputConfig={{
                      sx: {
                        width: 180,
                        '& .MuiFormHelperText-root.Mui-error': {
                          wordBreak: 'keep-all',
                          whiteSpace: 'nowrap',
                        },
                      },
                    }}
                    errors={errors}
                  />
                </Grid>
                <Grid item>
                  <FormItem
                    disabled={isStartTimeFixed}
                    defaultValue={new Date()}
                    name="startTime"
                    type="time"
                    control={control}
                    inputConfig={{
                      sx: { width: 160 },
                    }}
                    errors={errors}
                  />
                </Grid>
                {/* <Grid item>
                  <Typography>{t('campaign.schedule_notice')}</Typography>
                </Grid> */}
              </Grid>
              <Grid container item xs={12} spacing={1} alignItems="flex-start">
                <Grid item>
                  <FormItem
                    label={t('general.end_time')}
                    defaultValue={moment().startOf('day').toDate()}
                    name="endDate"
                    type="date"
                    control={control}
                    inputConfig={{
                      sx: {
                        width: 180,
                        '& .MuiFormHelperText-root.Mui-error': { wordBreak: 'keep-all' },
                        whiteSpace: 'nowrap',
                      },
                    }}
                    errors={errors}
                  />
                </Grid>
                <Grid item>
                  <FormItem
                    defaultValue={new Date()}
                    name="endTime"
                    type="time"
                    control={control}
                    inputConfig={{
                      sx: { width: 160 },
                    }}
                    errors={errors}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Section>

          <Section title={t('general.mail_notify')}>
            <Grid container spacing={2}>
              <Grid item container xs={12} sm={6}>
                <Box>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Typography>{`*${t('general.training_invite')}`}</Typography>
                    </Grid>

                    <Grid item container>
                      <Grid item>
                        <FormItem
                          label={t('general.send_training_invite')}
                          defaultValue={true}
                          name="sendInvite"
                          type="checkbox"
                          control={control}
                          inputConfig={inputConfig}
                          errors={errors}
                          disabled={true}
                        />
                      </Grid>

                      <Grid item>
                        <Button
                          color="white"
                          onClick={() => {
                            setPreview({
                              details: trainingMail,
                              title: t('general.training_invite_preview'),
                            })
                          }}
                        >
                          {t('general.preview_template')}
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid item container xs={12} sm={6} spacing={1}>
                <Grid item xs={12}>
                  <Typography>{`*${t('general.notify_email')}`}</Typography>
                </Grid>
                <Grid item container alignItems="center" wrap="nowrap" spacing={1}>
                  <Grid item container alignItems="center" sx={{ width: 'auto' }}>
                    <Grid item>
                      <FormItem
                        label={t('general.training_reminder_notify')}
                        defaultValue={false}
                        name="reminderNotify"
                        type="checkbox"
                        control={control}
                        inputConfig={inputConfig}
                        errors={errors}
                      />
                    </Grid>
                    <Grid item>
                      <Button
                        color="white"
                        onClick={() => {
                          setPreview({
                            details: remindMail,
                            title: t('general.training_reminder_notify_preview'),
                          })
                        }}
                      >
                        {t('general.preview_template')}
                      </Button>
                    </Grid>
                  </Grid>
                  <Grid item>
                    <IconButton
                      disabled={!isReminderNotifyChecked}
                      onClick={reminderScheduleAddHandler}
                    >
                      <AddCircleOutlineIcon />
                    </IconButton>
                  </Grid>
                </Grid>

                <FormItem
                  defaultValue={[]}
                  disabled={!isReminderNotifyChecked}
                  name="reminderScheduleList"
                  type="controlList"
                  control={control}
                  inputConfig={{
                    ...inputConfig,
                    component: scheduleComponent,
                  }}
                  errors={errors}
                />

                <Grid item container>
                  <Grid item>
                    <FormItem
                      label={t('general.training_complete_notify')}
                      defaultValue={false}
                      name="completeNotify"
                      type="checkbox"
                      control={control}
                      inputConfig={inputConfig}
                      errors={errors}
                    />
                  </Grid>
                  <Grid item>
                    <Grid item>
                      <Button
                        color="white"
                        onClick={() => {
                          setPreview({
                            details: completionMail,
                            title: t('general.training_complete_notify_preview'),
                          })
                        }}
                      >
                        {t('general.preview_template')}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Section>

          <Button sx={{ float: 'right', m: 2 }} type="submit">
            {t('general.create_campaign')}
          </Button>
        </Box>
      </Card>

      <EmailDialog
        open={!!preview.details}
        details={preview.details}
        title={preview.title}
        onClose={() => setPreview({})}
      />

      <CourseDialog
        open={openCourse}
        courseId={selectedCourse?.id}
        title={selectedCourse?.label}
        onClose={() => setOpenCourse(false)}
      />
    </Container>
  )
}

export default connect(mapStateToProps)(CampaignCreate)
